/**
 * Lightweight transform-history store used by the Studio side panel.
 *
 * Auth users: history comes from `saved_works` in Supabase. This module is
 * only the localStorage fallback used for guests and offline.
 *
 * Items are capped at `MAX_ITEMS`. The newest entry is always at index 0.
 */

const KEY = "vml_transform_history";
export const MAX_HISTORY_ITEMS = 10;

export interface GuestHistoryItem {
  id: string;
  inputSnippet: string;
  outputText?: string;
  outputImageUrl?: string;
  mode: "write" | "create" | "code";
  createSubTab?: "casual" | "business" | "playful" | "repurpose" | "image";
  personaSlug?: string;
  personaName?: string;
  timestamp: number;
}

export function readGuestHistory(): GuestHistoryItem[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.slice(0, MAX_HISTORY_ITEMS) as GuestHistoryItem[];
  } catch {
    return [];
  }
}

export function appendGuestHistory(item: Omit<GuestHistoryItem, "id" | "timestamp">) {
  try {
    const existing = readGuestHistory();
    const next: GuestHistoryItem[] = [
      { ...item, id: crypto.randomUUID(), timestamp: Date.now() },
      ...existing,
    ].slice(0, MAX_HISTORY_ITEMS);
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    // quota exceeded — silent
  }
}

export function clearGuestHistory() {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // ignore
  }
}
